<script lang="ts" setup>
import { TOAST_REGION_ATTRIBUTE } from '~/utils/toastRegion.constants'
import { AlertTriangleIcon, CheckCircleIcon, InfoIcon, XCircleIcon, XIcon } from '@lucide/vue'
import { useToast } from '@/composables/useToast'
import type { Toast } from '@/composables/Toast'

const { toasts, remove } = useToast()

const icons = {
  error: XCircleIcon,
  neutral: InfoIcon,
  success: CheckCircleIcon,
  warning: AlertTriangleIcon
}

const iconColors = {
  error: 'text-danger-text',
  neutral: 'text-info-text',
  success: 'text-success-text',
  warning: 'text-warning-text'
}

const triggerAction = (toast: Toast) => {
  toast.action?.onClick()
  remove(toast.id)
}

const toastRegionAttributes = { [TOAST_REGION_ATTRIBUTE]: '' }
</script>

<template>
  <div
    aria-live="assertive"
    class="fixed flex inset-0 items-end pointer-events-none px-4 py-6 sm:items-start sm:p-6 z-(--z-index-ds-toast)"
    v-bind="toastRegionAttributes"
  >
    <div class="flex flex-col items-center sm:items-end space-y-4 w-full">
      <TransitionGroup
        tag="div"
        class="flex flex-col items-center sm:items-end space-y-4 w-full"
        enter-active-class="transform ease-out duration-300 transition"
        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
        move-class="transition ease-in-out duration-500"
      >
        <div
          v-for="notif in toasts"
          :key="notif.id"
          class="bg-surface-canvas dark:bg-surface-level-800 dark:ring-focus-inverse/10 max-w-sm overflow-hidden pointer-events-auto ring-1 ring-focus-ink/5 rounded-lg shadow-lg w-full"
        >
          <div class="p-4">
            <div class="flex items-start">
              <div class="shrink-0">
                <component
                  :is="icons[notif.type]"
                  class="size-6"
                  :class="iconColors[notif.type]"
                  aria-hidden="true"
                />
              </div>

              <div class="flex-1 ml-3 pt-0.5 w-0">
                <p class="dark:text-content-inverse font-medium text-content-level-900 text-sm">
                  {{ notif.message }}
                </p>

                <p
                  v-if="notif.description"
                  class="dark:text-content-level-400 mt-1 text-content-level-500 text-sm"
                >
                  {{ notif.description }}
                </p>

                <div
                  v-if="notif.action"
                  class="mt-3"
                >
                  <button
                    type="button"
                    class="font-medium hover:text-primary-dark text-primary text-sm"
                    @click="() => triggerAction(notif)"
                  >
                    {{ notif.action.label }}
                  </button>
                </div>
              </div>

              <div class="flex ml-4 shrink-0">
                <button
                  type="button"
                  class="bg-surface-canvas dark:bg-surface-level-800 dark:hover:text-content-inverse dark:text-content-level-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary hover:text-content-level-500 inline-flex rounded-md text-content-level-400"
                  @click="() => remove(notif.id)"
                >
                  <span class="sr-only">
                    {{ $t('common.actions.close') }}
                  </span>

                  <XIcon
                    class="size-5"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </div>
</template>

<style scoped>
.v-leave-active {
  position: absolute;
}
</style>
